/**
 * PageRenderer — roteia páginas, keep-alive (dashboard/chat) e shell de layout.
 */
import React, { useEffect, useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { getRouteStateFromPathname } from '../utils/routeUtils';
import { SettingsLayout, SettingsHub } from '../components/layout/SettingsLayout';
import { SETTINGS_CONTENT_PAGES } from '../components/layout/sidebar/config';
import { MOBILE_CONTENT_BOTTOM_PAD_CLASS } from '../components/layout/layoutNavTokens';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import { cn } from '@/lib/utils';
import type { Page } from '../types';

import DashboardPage from '../pages/DashboardPage';
import KanbanPage from '../pages/Kanban';
import SubscriptionPage from '../pages/Subscription';
import OnboardingPage from '../pages/OnboardingPage';
import ProfilePage from '../pages/Profile';
import MonitoringPage from '../pages/Controle';
import ConnectedChannelsPage from '../pages/ConnectedChannels';
import TeamPage from '../pages/Team';
import BillingPage from '../pages/Billing';
import TrackingSettingsPage from '../pages/TrackingSettings';
import ContactsPage from '../pages/Contacts';
import ProductsPage from '../pages/Products';
import FunnelManagePage from '../pages/FunnelManagePage';
import ChatPage from '../pages/ChatPage';
import ConnectionsPage from '../pages/Connections';
import TagsSettingsPage from '../pages/TagsSettings';
import QuickRepliesSettingsPage from '../pages/QuickRepliesSettings';
import PixKeySettingsPage from '../pages/PixKeySettings';
import LossReasonsSettingsPage from '../pages/LossReasonsSettings';
import OriginsSettingsPage from '../pages/OriginsSettings';
import WebhooksSettingsPage from '../pages/WebhooksSettings';
import AutoMovementPage from '../pages/AutoMovementPage';
import FollowUpPage from '../pages/FollowUpPage';
import TasksPage from '../pages/Tasks';
import SuperadminDashboardPage from '../pages/superadmin/Dashboard';
import SuperadminOrganizationsPage from '../pages/superadmin/Organizations';
import SuperadminOrganizationDetailPage from '../pages/superadmin/OrganizationDetail';
import SuperadminFinancePage from '../pages/superadmin/Finance';
import SuperadminWhatsappPage from '../pages/superadmin/Whatsapp';
import SuperadminIntegrationsPage from '../pages/superadmin/Integrations';
import SuperadminCommunicationsPage from '../pages/superadmin/Communications';
import SuperadminTicketsPage from '../pages/superadmin/Tickets';
import SuperadminCommsWhatsappPage from '../pages/superadmin/CommsWhatsapp';
import SuperadminPartnersPage from '../pages/superadmin/Partners';
import SupportPage from '../pages/Support';
import ParceiroPage from '../pages/Parceiro';
import ParceiroAplicarPage from '../pages/ParceiroAplicar';

type PageComponent = React.ComponentType;

/** Páginas sob demanda (dashboard/chat ficam no keep-alive — não entram aqui). */
const ON_DEMAND_PAGES: Partial<Record<Page, PageComponent>> = {
    kanban: KanbanPage,
    contacts: ContactsPage,
    'create-funnel': FunnelManagePage,
    'edit-funnel': FunnelManagePage,
    tasks: TasksPage,
    'follow-up': FollowUpPage,
    support: SupportPage,
    parceiro: ParceiroPage,
    'parceiro-aplicar': ParceiroAplicarPage,
    superadmin: SuperadminDashboardPage,
    'superadmin-organizations': SuperadminOrganizationsPage,
    'superadmin-organization-detail': SuperadminOrganizationDetailPage,
    'superadmin-finance': SuperadminFinancePage,
    'superadmin-invoices': SuperadminFinancePage,
    'superadmin-whatsapp': SuperadminWhatsappPage,
    'superadmin-integrations': SuperadminIntegrationsPage,
    'superadmin-communications': SuperadminCommunicationsPage,
    'superadmin-tickets': SuperadminTicketsPage,
    'superadmin-comms-whatsapp': SuperadminCommsWhatsappPage,
    'superadmin-partners': SuperadminPartnersPage,
};

const SETTINGS_PAGES: Partial<Record<Page, PageComponent>> = {
    profile: ProfilePage,
    team: TeamPage,
    monitoring: MonitoringPage,
    whatsapp: ConnectedChannelsPage,
    billing: BillingPage,
    tracking: TrackingSettingsPage,
    products: ProductsPage,
    connections: ConnectionsPage,
    'settings-tags': TagsSettingsPage,
    'settings-quick-replies': QuickRepliesSettingsPage,
    'settings-pix': PixKeySettingsPage,
    'settings-loss-reasons': LossReasonsSettingsPage,
    'settings-origins': OriginsSettingsPage,
    'settings-webhooks': WebhooksSettingsPage,
    'settings-auto-movement': AutoMovementPage,
};

const LEGACY_REDIRECTS: Partial<Record<Page, Page>> = {
    'superadmin-users': 'superadmin-organizations',
    'superadmin-invoices': 'superadmin-finance',
};

const APP_SHELL_PAGES: Page[] = ['chat', 'kanban', 'contacts', 'tasks'];

const PADDED_SHELL = cn('p-4 md:p-6 md:pb-8 lg:p-8', MOBILE_CONTENT_BOTTOM_PAD_CLASS);

const keepAliveVisibility = (active: boolean) =>
    active ? 'z-10' : 'z-0 invisible pointer-events-none';

function contentShellClass(page: Page): string {
    if (page === 'create-funnel' || page === 'edit-funnel') {
        return cn('flex flex-col overflow-hidden', PADDED_SHELL);
    }
    if (APP_SHELL_PAGES.includes(page) || page === 'onboarding') {
        return 'flex flex-col overflow-hidden';
    }
    if (page === 'subscription') {
        return cn('overflow-y-auto scrollbar-premium', MOBILE_CONTENT_BOTTOM_PAD_CLASS);
    }
    if (page === 'settings' || SETTINGS_CONTENT_PAGES.includes(page)) {
        return 'overflow-y-auto';
    }
    return cn('overflow-y-auto', PADDED_SHELL);
}

/** Keep-alive: monta na 1ª visita e permanece (estado preservado). */
function useEverOpenedPage(page: Page, currentPage: Page): boolean {
    const [opened, setOpened] = useState(
        () =>
            currentPage === page ||
            getRouteStateFromPathname(window.location.pathname).page === page,
    );
    useEffect(() => {
        if (currentPage === page) setOpened(true);
    }, [currentPage, page]);
    return opened;
}

const PageRenderer: React.FC = () => {
    const { currentPage, setCurrentPage } = useAppContext();
    const chatEverOpened = useEverOpenedPage('chat', currentPage);
    const dashboardEverOpened = useEverOpenedPage('dashboard', currentPage);

    useEffect(() => {
        const dest = LEGACY_REDIRECTS[currentPage];
        if (dest) setCurrentPage(dest);
    }, [currentPage, setCurrentPage]);

    const renderPage = () => {
        if (currentPage === 'onboarding') {
            return (
                <div className="h-full min-h-0">
                    <OnboardingPage />
                </div>
            );
        }

        if (currentPage === 'subscription') {
            return (
                <div className="h-full min-h-0 max-md:p-0 max-md:pb-0 md:p-6 md:pb-16">
                    <SubscriptionPage />
                </div>
            );
        }

        if (currentPage === 'settings') {
            return <SettingsHub />;
        }

        if (SETTINGS_CONTENT_PAGES.includes(currentPage)) {
            const Content = SETTINGS_PAGES[currentPage];
            return (
                <SettingsLayout activeTab={currentPage}>
                    {Content ? <Content /> : null}
                </SettingsLayout>
            );
        }

        const PageComp = ON_DEMAND_PAGES[currentPage];
        if (PageComp) return <PageComp />;

        return <DashboardPage />;
    };

    const showOnDemand = currentPage !== 'dashboard' && currentPage !== 'chat';

    return (
        <div className="relative h-full min-h-0 w-full">
            {dashboardEverOpened && (
                <div
                    className={cn(
                        'absolute inset-0 overflow-y-auto overscroll-y-contain bg-background p-s4 md:p-6 md:pb-8 lg:p-8',
                        MOBILE_CONTENT_BOTTOM_PAD_CLASS,
                        keepAliveVisibility(currentPage === 'dashboard'),
                    )}
                    aria-hidden={currentPage !== 'dashboard'}
                >
                    <DashboardPage />
                </div>
            )}

            {chatEverOpened && (
                <div
                    className={cn(
                        'absolute inset-0 flex min-h-0 flex-col overflow-hidden bg-background',
                        keepAliveVisibility(currentPage === 'chat'),
                    )}
                    aria-hidden={currentPage !== 'chat'}
                >
                    <ErrorBoundary
                        fallback={
                            <div className="flex h-full items-center justify-center p-4 text-sm text-secondary">
                                Erro ao carregar conversas. Toque em Tentar novamente ou recarregue a página.
                            </div>
                        }
                    >
                        <ChatPage />
                    </ErrorBoundary>
                </div>
            )}

            {showOnDemand && (
                <div className={cn('absolute inset-0 z-20 bg-background', contentShellClass(currentPage))}>
                    {renderPage()}
                </div>
            )}
        </div>
    );
};

export default PageRenderer;
